import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { GuaranteeRows } from './GuaranteeRows';
import { useBranches } from './useBranches';
import { useGuaranteeActions } from './useGuaranteeActions';
import { fetchAllGuarantees } from './fetchAllGuarantees';

/**
 * Garanties qu'aucune branche ne rattache.
 *
 * La reprise de données a laissé sans branche toutes les garanties qu'aucun tunnel
 * n'utilisait. Elles n'apparaissent dans aucune branche et ne sont donc proposées nulle
 * part : cet écran est le seul endroit d'où on peut les retrouver et les rattacher. Il
 * se vide de lui-même à mesure que le stock est traité.
 */
export const UnassignedGuarantees: FC = () => {
  const navigate = useNavigate();
  const branches = useBranches();

  const fetchGuarantees = useCallback(() => fetchAllGuarantees({ unassignedOnly: true }), []);
  const { data, isLoading, error, reload } = useAsyncResource(fetchGuarantees);

  const actions = useGuaranteeActions({ branches, onChanged: reload });

  const guarantees = data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/back-office/branches')}
        className="inline-flex w-fit items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
      >
        <ArrowLeft size={16} />
        Retour aux branches
      </button>

      <h1 className="text-2xl font-bold text-zinc-800">Garanties sans branche</h1>

      <BlockCard
        title="À rattacher"
        description="Garanties héritées de la reprise, rattachées à aucune branche"
        icon={<AlertTriangle className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {data?.totalCount ?? guarantees.length}
          </span>
        }
        isLoading={isLoading}
        error={error}
        isEmpty={guarantees.length === 0}
        emptyLabel="Plus aucune garantie orpheline — tout est rattaché"
        flushBody
      >
        <p className="border-b border-gray-100 px-5 py-3 text-xs text-gray-500">
          Tant qu'une garantie n'a pas de branche, aucun tunnel ne peut la proposer.
          Ouvrez-la pour lui en attribuer une.
        </p>

        <GuaranteeRows
          guarantees={guarantees}
          onEdit={actions.openGuarantee}
          onDelete={actions.requestDelete}
          showBranch
        />
      </BlockCard>

      <ConfirmationOverlay
        isOpen={Boolean(actions.guaranteeToDelete)}
        onClose={actions.cancelDelete}
        onConfirm={actions.confirmDelete}
        isLoading={actions.isDeleting}
        title="Supprimer cette garantie ?"
        description={`« ${actions.guaranteeToDelete?.shortLabel ?? ''} » sera définitivement retirée du référentiel.`}
        confirmLabel="Supprimer"
      />
    </div>
  );
};

export default UnassignedGuarantees;
